import { DocumentBase } from "@/services/backend/documents/types/base";
import {
  addByItemBelow,
  ByItem,
  ByItemKind,
  Column,
  ColumnKind,
  moveByItem,
  newByItem,
  PricingBy,
  PricingByDimension,
  PricingByPatch,
  pricingByColumns,
  removeByItem,
  updateByItem,
} from "../types/pricing";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellDecimalInput,
  ETableCellInput,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import {
  DragNDropWrapper,
  ETableCellDragNDropGrip,
} from "@/shared/components/tables/e-table-cell-drag-n-drop";
import { DragEndEvent } from "@dnd-kit/core";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Button } from "@/shared/components/ui/button";
import { EllipsisVertical, Plus, Trash2 } from "lucide-react";
import { Switch } from "@/shared/components/ui/switch";
import { Label } from "@/shared/components/ui/label";
import { Field } from "@/shared/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

export type PricingByComponentProps = {
  document: DocumentBase;
  pricingBy: PricingBy;
  onPatch: (patch: PricingByPatch) => void;
  title?: string;
  disabled?: boolean;
  /** Offers to derive the pricing by from the positions. */
  usePositionsSwitch?: boolean;
  /**
   * Offers to activate the pricing by. Without it, an inactive pricing
   * by renders nothing.
   */
  activeSwitch?: boolean;
};

/** PricingByCardConfig are the props of a {@link PricingByCard} a service kind sets. */
export type PricingByCardConfig = Pick<
  PricingByComponentProps,
  "usePositionsSwitch" | "activeSwitch"
>;

export function PricingByCard({
  pricingBy,
  onPatch,
  title,
  disabled,
  usePositionsSwitch = true,
  activeSwitch = true,
}: PricingByComponentProps) {
  if (!activeSwitch && !pricingBy.active) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {title ||
            (pricingBy.dimension === PricingByDimension.Area
              ? t("Preise nach Bereich")
              : t("Preise nach Gruppe"))}
        </CardTitle>
        <CardAction className="flex items-center gap-4">
          {pricingBy.active && !pricingBy.usePositions && (
            <Select
              value={pricingBy.dimension}
              onValueChange={(dimension) =>
                onPatch({ dimension: dimension as PricingByDimension })
              }
              disabled={disabled}
            >
              <SelectTrigger aria-label={t("Aufteilung")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PricingByDimension.Group}>
                  {t("Nach Gruppe")}
                </SelectItem>
                <SelectItem value={PricingByDimension.Area}>
                  {t("Nach Bereich")}
                </SelectItem>
              </SelectContent>
            </Select>
          )}
          {pricingBy.active && usePositionsSwitch && (
            <Field orientation="horizontal">
              <Switch
                id="pricing-by-use-positions"
                checked={pricingBy.usePositions}
                onCheckedChange={(usePositions) => onPatch({ usePositions })}
                disabled={disabled}
              />
              <Label htmlFor="pricing-by-use-positions">
                {t("Daten aus Positionen")}
              </Label>
            </Field>
          )}
          {activeSwitch && (
            <Field orientation="horizontal">
              <Switch
                id="pricing-by-active"
                checked={pricingBy.active}
                onCheckedChange={(active) =>
                  // an active pricing by needs a dimension
                  onPatch(
                    active && !pricingBy.dimension
                      ? { active, dimension: PricingByDimension.Group }
                      : { active },
                  )
                }
                disabled={disabled}
              />
              <Label htmlFor="pricing-by-active">{t("Aktiv")}</Label>
            </Field>
          )}
        </CardAction>
      </CardHeader>
      {pricingBy.active && (
        <CardContent>
          {/* the backend derives the items from the positions and rejects a
            manual patch of them */}
          <PricingByItems
            pricingBy={pricingBy}
            onPatch={onPatch}
            disabled={disabled}
            readOnly={pricingBy.usePositions}
          />
        </CardContent>
      )}
    </Card>
  );
}

function PricingByItems({
  pricingBy,
  onPatch,
  disabled,
  readOnly,
}: {
  pricingBy: PricingBy;
  onPatch: (p: PricingByPatch) => void;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  const columns = pricingByColumns(pricingBy.items);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const items = moveByItem(
      pricingBy.items,
      Number(active.id),
      Number(over.id),
    );
    if (items !== pricingBy.items) {
      onPatch({ items });
    }
  };

  return (
    <DragNDropWrapper
      items={pricingBy.items.map((i) => i.order)}
      handleDragEnd={handleDragEnd}
      disabled={disabled || readOnly}
    >
      <ETable>
        <ETableHeader>
          <ETableRow>
            <ETableHead />
            <ETableHead className="w-full">{t("Name")}</ETableHead>
            {columns.map((column, c) => (
              // columns have no identity of their own, their index is
              // their key in the backend as well
              <ETableHead key={c}>{column.title}</ETableHead>
            ))}
            <ETableHead>{t("Gesamt")}</ETableHead>
            <ETableHead />
          </ETableRow>
        </ETableHeader>
        <ETableBody>
          {pricingBy.items.length === 0 && (
            // without items, the actions menu adds the first one
            <ETableRow>
              <ETableCell aria-hidden="true" colSpan={columns.length + 3} />
              <PricingByItemActionsCell
                pricingBy={pricingBy}
                columns={columns}
                onPatch={onPatch}
                disabled={disabled || readOnly}
              />
            </ETableRow>
          )}
          {pricingBy.items.map((item) => (
            <PricingByItemRow
              key={item.order}
              item={item}
              pricingBy={pricingBy}
              columns={columns}
              onPatch={onPatch}
              disabled={disabled}
              readOnly={readOnly}
            />
          ))}
        </ETableBody>
      </ETable>
    </DragNDropWrapper>
  );
}

function PricingByItemRow({
  item,
  pricingBy,
  columns,
  onPatch,
  disabled,
  readOnly,
}: {
  item: ByItem;
  pricingBy: PricingBy;
  columns: Column[];
  onPatch: (p: PricingByPatch) => void;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  switch (item.kind) {
    case ByItemKind.Total:
      return (
        <ETableRow className="font-bold">
          <ETableCell aria-hidden="true" />
          <ETableCellInput
            value={item.text}
            onChange={(e) =>
              onPatch({
                items: updateByItem(pricingBy.items, item.order, {
                  ...item,
                  text: e.target.value,
                }),
              })
            }
            fieldName={`items.${item.order}.text`}
            colSpan={columns.length + 1}
            readOnly={readOnly}
            disabled={disabled}
          />
          <ETableCellDecimalInput
            value={item.total}
            onValueChange={() => null}
            precision={2}
            readOnly
          />
          <PricingByItemActionsCell
            item={item}
            pricingBy={pricingBy}
            columns={columns}
            onPatch={onPatch}
            disabled={disabled || readOnly}
          />
        </ETableRow>
      );
    default:
      return (
        <ETableRow>
          <ETableCellDragNDropGrip
            id={item.order}
            disabled={disabled || readOnly}
          />
          <ETableCellInput
            value={item.text}
            onChange={(e) =>
              onPatch({
                items: updateByItem(pricingBy.items, item.order, {
                  ...item,
                  text: e.target.value,
                }),
              })
            }
            className="w-full"
            fieldName={`items.${item.order}.text`}
            readOnly={readOnly}
            disabled={disabled}
          />
          {(item.columns ?? []).map((column, c) =>
            column.kind === ColumnKind.SubTotal ? (
              <ETableCellDecimalInput
                // columns have no identity of their own, see the header
                key={c}
                value={column.value}
                onValueChange={() => null}
                precision={2}
                readOnly
              />
            ) : (
              // a modifier column is entered signed, a discount negative
              <ETableCellDecimalInput
                key={c}
                value={column.value}
                onValueChange={(value) =>
                  onPatch({
                    items: updateByItem(pricingBy.items, item.order, {
                      ...item,
                      columns: (item.columns ?? []).map((other, o) =>
                        o === c ? { ...other, value } : other,
                      ),
                    }),
                  })
                }
                precision={2}
                fieldName={`items.${item.order}.columns.${c}.value`}
                readOnly={readOnly}
                disabled={disabled}
              />
            ),
          )}
          <ETableCellDecimalInput
            value={item.total}
            onValueChange={() => null}
            precision={2}
            readOnly
          />
          <PricingByItemActionsCell
            item={item}
            pricingBy={pricingBy}
            columns={columns}
            onPatch={onPatch}
            disabled={disabled || readOnly}
          />
        </ETableRow>
      );
  }
}

function PricingByItemActionsCell({
  item,
  pricingBy,
  columns,
  onPatch,
  disabled,
}: {
  item?: ByItem;
  pricingBy: PricingBy;
  columns: Column[];
  onPatch: (p: PricingByPatch) => void;
  disabled?: boolean;
}) {
  const hasTotal = pricingBy.items.some((i) => i.kind === ByItemKind.Total);
  const options = [
    { label: t("Posten"), item: () => newByItem(ByItemKind.Default, columns) },
    ...(hasTotal
      ? []
      : [
          {
            label: t("Gesamtpreis"),
            item: () => newByItem(ByItemKind.Total, []),
          },
        ]),
  ];

  return (
    <ETableCell className="w-8 p-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button variant="ghost" className="w-8 rounded-none">
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <DropdownMenuLabel>{t("Darunter hinzufügen")}</DropdownMenuLabel>
            {options.map((option) => (
              <DropdownMenuItem
                key={option.label}
                onSelect={() =>
                  onPatch({
                    items: addByItemBelow(
                      pricingBy.items,
                      item?.order ?? 0,
                      option.item(),
                    ),
                  })
                }
              >
                <Plus />
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
          {item && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() =>
                  onPatch({
                    items: removeByItem(pricingBy.items, item.order),
                  })
                }
              >
                <Trash2 />
                {t("Löschen")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </ETableCell>
  );
}
